import { inject } from 'vue';

import { cloneDeep } from 'lodash';

import { defineStore } from 'pinia';
import { CreateMessageAndReturnMessageIdCommand } from 'src/stores/message/commands/CreateMessageAndReturnMessageIdCommand';
import { SendAndMemorizeMessage } from 'src/stores/message/commands/SendAndMemorizeMessage';
import { SendMessage } from 'src/stores/message/commands/SendMessage';
import { SendNewMessage } from 'src/stores/message/commands/SendNewMessage';
import { MessageQuery } from 'src/stores/message/queries';

import { CommandProcessIdHandling } from 'src/models/command';
import { Message, MessageRunType } from 'src/models/message';
import { SetTheStateForMultipleAggregates } from 'src/models/status/SetStatusCommand';
import { StatusEnum } from 'src/models/status/enums';

import { AggregateComparator } from 'src/util/aggregate/AggregateComparator';
import { AggregateMerger } from 'src/util/aggregate/AggregateMerger';
import {
	ApplyTheStatusForTheAggregateIdsPayload,
	aggregateIdWithRemovedIdoAndInstanceId,
	aggregateIdWithUnsetSourceAndTargetContainerId,
	applyTheStatusForTheAggregateIdsInRecordMaps,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { CommandBus } from 'src/util/command/CommandBus';

import { MessageCallback } from './callbacks';
import {
	CreateTheIdoLinkedMessagePayload,
	CreateTheMessagePayload,
	FetchMessagePayload,
	MessageStoreState,
	ResetPayload,
	SendMessagePayload,
	SendNewMessagePayload,
	SetMessageIsSendPayload,
	SetTheMessageStatePayload,
	UnsubscribeFetchMessagePayload,
} from './interfaces';

const normalizeAggregateId = (aggregateId: string): string =>
	aggregateIdWithUnsetSourceAndTargetContainerId(aggregateId);

const createDefaultState = (): MessageStoreState => ({
	initialLoadedMessages: new Map<string, Message>(),
	messageIsSendMap: new Map<string, boolean>(),
	messages: new Map<string, Message>(),
	newMessageAggregateId: '',
	newMessageCommunicationPartnerUserId: 0,
});

const resolveStateCommandPayload = (
	payload: SetTheMessageStatePayload | string
): { aggregateId: string; commandBus: CommandBus | undefined } => {
	if (typeof payload === 'string') {
		return {
			aggregateId: payload,
			commandBus: inject<CommandBus>('commandBus'),
		};
	}

	return {
		aggregateId: payload.aggregateId,
		commandBus: payload.commandBus ?? inject<CommandBus>('commandBus'),
	};
};

const parseAggregateIdFromCreateLinkedMessageResult = (result: unknown): string => {
	const rawAggregateId = (
		result as {
			data?: {
				data?: {
					aggregateId?: string;
				};
			};
		}
	).data?.data?.aggregateId;

	if (typeof rawAggregateId !== 'string') {
		return '';
	}

	return aggregateIdWithRemovedIdoAndInstanceId(rawAggregateId);
};

export const useMessageStore = defineStore('message', {
	actions: {
		applyTheStatusForTheAggregateIds(payload: ApplyTheStatusForTheAggregateIdsPayload): void {
			applyTheStatusForTheAggregateIdsInRecordMaps(
				[this.messages, this.initialLoadedMessages],
				payload.aggregateIds,
				payload.status
			);
		},
		createTheIdoLinkedMessage(payload: CreateTheIdoLinkedMessagePayload): void {
			void payload.commandBus
				?.sendCommand(
					new CreateMessageAndReturnMessageIdCommand({
						idoLinkings: [
							{
								idoId: String(payload.idoId),
								instanceId: String(payload.instanceId),
							},
						],
					})
				)
				?.then((result) => {
					this.setNewMessageAggregateId(parseAggregateIdFromCreateLinkedMessageResult(result));
				});
		},
		createTheMessage(payload: CreateTheMessagePayload): void {
			void payload.commandBus
				?.sendCommand(
					new CreateMessageAndReturnMessageIdCommand({
						idoLinkings: [],
					})
				)
				?.then((result) => {
					const aggregateId = (
						result as {
							data?: {
								data?: {
									aggregateId?: string;
								};
							};
						}
					).data?.data?.aggregateId;

					this.setNewMessageAggregateId(aggregateId ?? '');
				});
		},
		fetchMessage(payload: FetchMessagePayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				return payload.queryStore.query<Message>({
					callback: new MessageCallback(
						(aggregateId) => this.messages.get(aggregateId),
						(message) => this.setMessage(message)
					),
					createTime: new Date(),
					query: new MessageQuery(payload.aggregateId),
					responseType: 'json',
					runType: MessageRunType,
				});
			}

			this.setMessage(null);
			return Promise.resolve();
		},
		message(aggregateId: string): Message | undefined {
			return this.messages.get(aggregateId);
		},
		messageIsSend(aggregateId: string): boolean | undefined {
			return this.messageIsSendMap.get(aggregateId);
		},
		reset(payload: ResetPayload): void {
			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);

			this.messages.delete(normalizedAggregateId);
			this.initialLoadedMessages.delete(normalizedAggregateId);
			this.messageIsSendMap.delete(normalizedAggregateId);
			this.newMessageAggregateId = '';
		},
		async sendMessage(payload: SendMessagePayload): Promise<void> {
			this.setMessageIsSend({
				aggregateId: payload.aggregateId,
				payload: false,
			});

			const sendCommand =
				payload.memorize === true
					? new SendAndMemorizeMessage(
							payload.aggregateId,
							payload.aggregateVersion,
							payload.authenticatedUserId,
							payload.communicationPartnerUserId,
							payload.messageContent
						)
					: new SendMessage(
							payload.aggregateId,
							payload.aggregateVersion,
							payload.authenticatedUserId,
							payload.communicationPartnerUserId,
							payload.messageContent
						);

			await payload.commandBus?.sendCommand(sendCommand)?.then(() => {
				this.setMessageIsSend({
					aggregateId: payload.aggregateId,
					payload: true,
				});
			});
		},
		async sendNewMessage(payload: SendNewMessagePayload): Promise<void> {
			this.setMessageIsSend({
				aggregateId: payload.aggregateId,
				payload: false,
			});

			await payload.commandBus
				?.sendCommand(
					new SendNewMessage(
						payload.aggregateId,
						payload.addressee,
						payload.message,
						payload.memorize === true
					)
				)
				?.then(() => {
					this.setMessageIsSend({
						aggregateId: payload.aggregateId,
						payload: true,
					});
				});
		},
		setMessage(payload: Message | null): void {
			if (payload === null) {
				return;
			}

			const normalizedAggregateId = normalizeAggregateId(payload.aggregateId);
			const messageFromCollection = this.messages.get(normalizedAggregateId);
			const initialLoadedMessageFromCollection = this.initialLoadedMessages.get(normalizedAggregateId);

			if (messageFromCollection !== undefined && !AggregateComparator.isEqual(messageFromCollection, payload)) {
				return;
			}

			if (messageFromCollection !== undefined && initialLoadedMessageFromCollection !== undefined) {
				const mergedMessage = AggregateMerger.merge(
					initialLoadedMessageFromCollection,
					messageFromCollection,
					payload
				) as Message;

				this.messages.set(normalizeAggregateId(mergedMessage.aggregateId), cloneDeep(mergedMessage));
			} else {
				this.messages.set(normalizedAggregateId, payload);
			}

			this.initialLoadedMessages.set(normalizedAggregateId, cloneDeep(payload));
		},
		setMessageIsSend(payload: SetMessageIsSendPayload): void {
			this.messageIsSendMap.set(normalizeAggregateId(payload.aggregateId), payload.payload);
		},
		setNewMessageAggregateId(payload: string): void {
			this.newMessageAggregateId = payload || '';
		},
		setNewMessageCommunicationPartnerUserId(payload: number): void {
			this.newMessageCommunicationPartnerUserId = payload || 0;
		},
		setTheMessageStateToReady(payload: SetTheMessageStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.READY
				)
			);
		},
		setTheMessageStateToSeen(payload: SetTheMessageStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.SEEN
				)
			);
		},
		setTheMessageStateToUnseen(payload: SetTheMessageStatePayload | string): void {
			const resolvedPayload = resolveStateCommandPayload(payload);

			void resolvedPayload.commandBus?.sendCommand(
				new SetTheStateForMultipleAggregates(
					[resolvedPayload.aggregateId],
					CommandProcessIdHandling.IGNORING,
					StatusEnum.UNSEEN
				)
			);
		},
		async unsubscribeFetchMessage(payload: UnsubscribeFetchMessagePayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.unsubscribeQuery(new MessageQuery(payload.aggregateId));
			}

			this.setMessage(null);
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
